import { FC, ReactElement, useState } from 'react'
import styled from 'styled-components'
import ReactPlayer from 'react-player'
// @ts-ignore
import { Scrollama, Step } from 'react-scrollama'
import SVG, { Props as SVGProps } from 'react-inlinesvg'
import screenfull from 'screenfull'

const MultiVideosModule: FC = (): ReactElement => {
  const [muted, setMuted] = useState(true)
  const [playing, setPlaying] = useState(false)
  const [player, setPlayer] = useState<ReactPlayer | null>(null)
  const [progress, setProgress] = useState(0)
  const [duration, setDuration] = useState(0)

  const handleWaypointEnter = () => {
    console.log('ENTER')
    setPlaying(true)
  }

  const handleWaypointLeave = () => {
    console.log('LEAVE')
    setPlaying(false)
  }

  const handleOnProgress = (e: any) => {
    setProgress(e.played)
  }

  const handleDuration = (duration: number) => {
    setDuration(duration)
  }

  return (
    <Container>
      <Quote>
        «Les mamans ont l'impression qu'elles sont fautives.»
        <QuoteLegend>Jason Fuchs, attaquant du LHC</QuoteLegend>
      </Quote>
      <UnmuteButtonContainer>
        <UnmuteButton
          onClick={() => {
            setMuted(!muted)
          }}
        >
          <UnmuteButtonIcon>
            <StyledSVG
              src={require(`./sound-icon${muted ? '' : '-muted'}.svg`)}
              title='Menu'
              color='#202346'
            />
          </UnmuteButtonIcon>
          {muted ? 'Activer' : 'Couper'} le son
        </UnmuteButton>
      </UnmuteButtonContainer>
      <Content>
        <Scrollama
          onStepEnter={handleWaypointEnter}
          onStepExit={handleWaypointLeave}
          offset={0.5}
        >
          <Step data={1}>
            <VideoContainer active={playing}>
              <div style={{position: 'relative'}}>
              <ReactPlayer
                onDuration={handleDuration}
                onProgress={handleOnProgress}
                ref={setPlayer}
                height={'auto'}
                width={'100%'}
                muted={muted}
                playsinline
                playing={playing}
                url={'src'}
                // loop
              />
              <PlayStateContainer>
                <Line progress={progress}></Line>
              </PlayStateContainer>
              </div>
              <Legend>
                <ReplayIcon
                  onClick={() => {
                    if (player) {
                      player.seekTo(0)
                    }
                  }}
                >
                  <StyledSVG
                    src={require(`./replay-icon.svg`).default}
                    title='Menu'
                    color='#202346'
                  />
                </ReplayIcon>
                Natacha Allenbasch parle de la découverte de la maladie, de la culpabilité, et
                des difficultés à trouver des réponses.
              </Legend>
            </VideoContainer>
          </Step>
        </Scrollama>
      </Content>
    </Container>
  )
}

const UnmuteButtonIcon = styled.div`
  height: 20px;
  line-height: 0;
  display: inline-block;
  margin-right: 0.75em;
`

const PlayStateContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
`

const Line = styled.div<{ progress: number }>`
  width: ${({ progress }) => progress * 100}%;
  height: 4px;
  background-color: #ffe462;
`

const StyledSVG = styled(SVG)<SVGProps>`
  height: 100%;
  width: auto;
  & path,
  line,
  circle,
  rect,
  polyline {
    fill: ${({ color }) => color} !important;
    stroke-width: 0;
  }
`

const Container = styled.div`
  position: relative;
  background-color: #222233;
  font-family: var(--font-serif);
  font-size: 1.8rem;
  line-height: 3rem;
  min-height: 150vh;
  color: white;
  padding: 60px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const Content = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const Legend = styled.div`
  font-family: var(--font-sans-serif);
  font-size: 0.85em;
  color: #ffffff;
  line-height: 1.2;
  display: flex;
  margin-top: 20px;
`

const Quote = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-family: var(--font-source);
  font-weight: 700;
  font-size: 4rem;
  line-height: 1;
  max-width: 25vw;
  text-align: center;

  &:before {
    content: '';
    width: 100px;
    background-color: #ffffff;
    height: 1px;
    display: block;
    opacity: 0.25;
    margin-bottom: 20px;
  }

  &:after {
    content: '';
    width: 100px;
    background-color: #ffffff;
    height: 1px;
    display: block;
    opacity: 0.25;
    margin-top: 30px;
  }
`

const QuoteLegend = styled.div`
  font-family: var(--font-sans-serif);
  font-size: 1.5rem;
  font-weight: 400;
  opacity: 0.5;
  margin-top: 20px;
`

const VideoContainer = styled.div<{ active: boolean }>`
  position: relative;
  width: 100%;
  max-width: 600px;
  padding: 20px;
  box-sizing: border-box;
  transition: transform 0.5s ease-in-out, opacity 0.5s ease-in-out;
  transform: ${({ active }) => (active ? 'scale(1)' : 'scale(0.9)')};
  opacity: ${({ active }) => (active ? '1' : '0.5')};
  line-height: 0;
`

const UnmuteButtonContainer = styled.div`
  width: 100%;
  text-align: center;
`

const UnmuteButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background-color: #ffe462;
  color: #222233
  border: none;
  outline: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-family: var(--font-sans-serif);
  font-size: 0.75em;
  font-weight: 700;
`

const ReplayIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background-color: #ffe462;
  border: none;
  outline: none;
  padding: 10px 12px;
  border-radius: 5px;
  cursor: pointer;
  font-family: var(--font-sans-serif);
  font-size: 0.75em;
  font-weight: 700;
  width: 20px;
  margin-right: 10px;
`

export default MultiVideosModule
